import { useState, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import {
  buildCustomerActivityLink,
  deadlineBadgeVariant,
  deriveUpcomingRenewals,
  formatDeadline,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioRenewalsProps = ComponentProps<"section"> & {
  customers: PortfolioCustomerSummary[]
}

const PREVIEW_LIMIT = 8

// The CS heartbeat: every upcoming contract renewal / trial expiry, soonest
// first, each with the health read that decides the conversation — churn
// badge (hover for the reasons), license use, trend. Names deep-link into
// the account's Activity tab; hover a name for the licenses held.
//
// The dates come from lic.Licenses via the portfolio proc: RenewsAt is the
// Fenerum-mirrored renewal clock, TrialEndsAt the operator-managed trial
// clock. Customers the license domain doesn't cover yet simply don't
// appear — no HubSpot property ever carried these dates.
function PortfolioRenewals(props: PortfolioRenewalsProps) {
  const { customers, className, ...sectionProps } = props

  const [showAll, setShowAll] = useState(false)
  const [search, setSearch] = useState("")

  const renewals = deriveUpcomingRenewals(customers)
  const searchTerm = search.trim().toLowerCase()
  const matching =
    searchTerm === ""
      ? renewals
      : renewals.filter((customer) =>
          customer.name.toLowerCase().includes(searchTerm),
        )
  // A search looks across ALL renewals, not just the visible preview.
  const visible =
    showAll || searchTerm !== "" ? matching : matching.slice(0, PREVIEW_LIMIT)
  const hiddenCount = matching.length - visible.length

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Renewals"
        title="Renewals & trials"
        lead="Contract renewals and trial expiries, soonest first, with the money each decides and the usage state the conversation starts from."
        info="Contract renewals and trial expiries from the license board, soonest first — overdue ones stay visible for 45 days. The amount is what the customer billed the last 12 months: the money the renewal decides. The health signals next to each are the last 28 days of usage: the state the renewal or conversion conversation starts from. Hover a customer name for the licenses they hold."
      >
        {renewals.length > 0 && (
          <div className="ml-auto w-44">
            <Input
              type="search"
              size="xs"
              placeholder="Search customers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        {renewals.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No upcoming renewal or trial dates among the customers in scope —
            dates appear as licenses get renewal clocks or trials on the
            Licenses board.
          </p>
        ) : matching.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No customers match the search.
          </p>
        ) : (
          // Inline scroll: "Show all" must not stretch the whole page.
          <ul className="renewals-list max-h-72 overflow-y-auto pr-1">
            <li className="renewals-row usage-report-list-head" aria-hidden>
              <span className="min-w-[9rem] flex-1">Customer</span>
              <span className="renewals-signals">
                <span className="w-20 text-right">Billed 12 mo</span>
                <span className="w-20 text-right">Seats active</span>
                <span className="w-16 text-center">Trend</span>
                <span className="w-28 text-center">Churn risk</span>
                <span className="w-28 text-center">Renewal</span>
              </span>
            </li>
            {visible.map((customer) => (
              <li key={customer.externalCustomerId} className="renewals-row">
                {/* The licenses held ride along as a tooltip — the row itself
                  stays reserved for the usage signals. */}
                <Link
                  to={buildCustomerActivityLink(customer.externalCustomerId)}
                  iconSrc={null}
                  className="min-w-[9rem] flex-1 truncate font-medium"
                  title={customer.plan ?? undefined}
                >
                  {customer.name}
                </Link>
                {/* Fixed widths per signal, so the columns line up down the
                    list; the whole group wraps under the name when narrow. */}
                <span className="renewals-signals">
                  <span
                    className="w-20 text-right text-xs font-medium tabular-nums"
                    title="Billed the last 12 months — the money this renewal decides"
                  >
                    {customer.billed12M != null && customer.billed12M > 0
                      ? formatCompactAmount(
                          customer.billed12M,
                          customer.primaryCurrency,
                        )
                      : "—"}
                  </span>
                  <span className="w-20 whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
                    {customer.summary.activeUsers28d}/
                    {customer.summary.seatCount} seats
                  </span>
                  <UsageTrendBadge
                    trendPct={customer.summary.trendPct}
                    detail={formatTrendDetail(
                      customer.summary.weightedEvents28d ??
                        customer.summary.events28d,
                      customer.summary.weightedEventsPrev28d,
                    )}
                    className="w-16 justify-center"
                  />
                  <UsageChurnRiskBadge
                    level={customer.summary.churnRisk.level}
                    reasons={customer.summary.churnRisk.reasons}
                    className="w-28 justify-center"
                  />
                  {customer.deadline != null ? (
                    <Badge
                      className="w-28 justify-center"
                      variant={deadlineBadgeVariant(customer.deadline)}
                    >
                      {formatDeadline(customer.deadline)}
                    </Badge>
                  ) : (
                    <span className="w-28" />
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {searchTerm === "" &&
          (hiddenCount > 0 || showAll) &&
          renewals.length > 0 && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? "Show fewer"
                : `Show all ${renewals.length} (+${hiddenCount})`}
            </button>
          )}
      </UsageReportCard>
    </section>
  )
}

export default PortfolioRenewals
